import type { ReactNode } from 'react';

export enum EmptyStateActionVariant {
  primary = 'primary',
  secondary = 'secondary',
}

export interface EmptyStateAction {
  /** Visible button label. */
  label: string;
  /** Click handler. */
  onClick: () => void;
  /** Visual variant. Defaults to `primary`. */
  variant?: EmptyStateActionVariant;
  /** Optional `data-cy` test hook on the action button. */
  'data-cy'?: string;
}

export interface EmptyStateProps {
  /**
   * Path to an SVG asset (preferred) OR a custom React node for the illustration.
   * Omit entirely for illustration-less variants.
   */
  illustration?: string | ReactNode;
  /**
   * Pixel width of the illustration when `illustration` is a string path. Rendered
   * as a rem value so the asset scales with the user's root font size. Required
   * in practice for `illustration: string` — there is no default. Omit for
   * illustration-less variants or when passing a custom React node.
   */
  illustrationWidth?: number;
  /**
   * Pixel height of the illustration when `illustration` is a string path.
   * Optional — when omitted, the SVG's intrinsic aspect ratio is preserved.
   */
  illustrationHeight?: number;
  /**
   * Alt text for accessibility. Defaults to `''` so the image renders as
   * decorative (`aria-hidden="true"`). Pass a real string only when the
   * illustration carries information not already in the title/subtitle.
   */
  illustrationAlt?: string;

  /**
   * Icon-container variant used by Select dialogs (PV flows): an 80×80 circular
   * grey container wrapping a centered icon (48×48 by default). Mutually
   * exclusive with `illustration` — when both are passed, `illustration` wins.
   */
  iconContainer?: {
    /** The icon node to render inside the circular container (e.g. an `Md*` icon). */
    icon: ReactNode;
    /** Diameter of the circular container in pixels. Defaults to 80. */
    size?: number;
  };

  /** Title (h5-bold). */
  title: string;
  /** Optional subtitle (body, gray-darker). */
  subtitle?: string;

  /**
   * 0–3 actions rendered in a centered row. Each action gets its own button
   * with the variant specified by the action (defaults to `primary`).
   */
  actions?: EmptyStateAction[];

  /** Fills available height and centers the card (default false); shared replacement for the per-page centering wrapper. */
  centered?: boolean;

  className?: string;
  'data-cy'?: string;
}
